import { useContext, useMemo } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { useUserStore } from '@/stores/userStore';
import {
  googleAvatarUrlFrom,
  ownAvatarProps,
  type OwnAvatarProps,
} from '@/lib/ownAvatar';

export interface UseOwnAvatar extends OwnAvatarProps {
  color: string | null;
}

/**
 * Props for rendering the signed-in user's own `<ProfileAvatar>`.
 *
 * Every surface that draws the current user (Sidebar, mobile drawer, Settings)
 * goes through this so the Google-photo default resolves identically in all of
 * them — the previous behaviour applied it in onboarding only, so the photo
 * appeared during setup and then vanished on the dashboard.
 *
 * Onboarding's Profile Setup does NOT use this hook: it renders unsaved local
 * state (an emoji the user just picked, an image they just uploaded) that has
 * not reached the store yet.
 */
export function useOwnAvatar(): UseOwnAvatar {
  // Read the context directly instead of through useAuth(), which throws
  // outside an <AuthProvider>. The Google photo is a progressive enhancement
  // on an avatar that already renders fine without it, so a consumer mounted
  // without a provider (Storybook stories for Sidebar, isolated component
  // tests) should degrade to the emoji rather than crash.
  const user = useContext(AuthContext)?.user ?? null;
  const avatarEmoji = useUserStore((state) => state.avatarEmoji);
  const avatarColor = useUserStore((state) => state.avatarColor);
  const avatarImagePath = useUserStore((state) => state.avatarImagePath);

  return useMemo(
    () => ({
      ...ownAvatarProps({
        avatarImagePath,
        avatarEmoji,
        googleAvatarUrl: googleAvatarUrlFrom(user),
      }),
      color: avatarColor,
    }),
    [avatarImagePath, avatarEmoji, avatarColor, user],
  );
}
